/**
 * Ledger API client.
 * Calls the NK Ledger at https://ledger.nexkoins.com using the current
 * Firebase user's ID token.
 */
import { auth } from '../firebase/config';

const LEDGER_BASE = process.env.REACT_APP_LEDGER_API_URL || 'https://ledger.nexkoins.com';

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function req(path, opts = {}) {
  const headers = await authHeaders();
  const res = await fetch(`${LEDGER_BASE}${path}`, {
    credentials: 'include',
    headers: { ...headers, ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) {
    let err;
    try { err = await res.json(); } catch { err = { error: `HTTP ${res.status}` }; }
    const e = new Error(err.detail || err.error || `HTTP ${res.status}`);
    e.status = res.status;
    throw e;
  }
  if (res.status === 204) return null;
  return res.json();
}

export const ledgerApi = {
  base: LEDGER_BASE,
  me: () => req('/api/me'),
  entities: () => req('/api/entities'),
  entity: (id) => req(`/api/entities/${id}`),
  snapshot: (id) => req(`/api/entities/${id}/snapshot`),
  pnl: (id, { period = 'month', from, to } = {}) => {
    const qs = new URLSearchParams();
    if (from && to) { qs.set('from', from); qs.set('to', to); }
    else { qs.set('period', period); }
    return req(`/api/entities/${id}/pnl?${qs}`);
  },
  balanceSheet: (id, at) => {
    const qs = new URLSearchParams();
    if (at) qs.set('at', at);
    return req(`/api/entities/${id}/balance-sheet?${qs}`);
  },
  kpi: (id) => req(`/api/entities/${id}/kpi`),
  capexAssets: (id) => req(`/api/entities/${id}/capex/assets`),
  accounts: (id) => req(`/api/entities/${id}/accounts`),
  roiSummary: (id) => req(`/api/entities/${id}/roi-summary`),
  monthlyTrend: (id, months = 12) => req(`/api/entities/${id}/monthly-trend?months=${months}`),

  audit: (id, { recordType, action, limit = 50, offset = 0 } = {}) => {
    const qs = new URLSearchParams();
    if (recordType) qs.set('record_type', recordType);
    if (action) qs.set('action', action);
    qs.set('limit', String(limit));
    qs.set('offset', String(offset));
    return req(`/api/entities/${id}/audit?${qs}`);
  },
  bankSummary: (id) => req(`/api/entities/${id}/bank/summary`),
  newsletter: (id, month) => {
    const qs = new URLSearchParams();
    if (month) qs.set('month', month);
    return req(`/api/entities/${id}/newsletter?${qs}`);
  },

  treasuryOverview: (id) => req(`/api/entities/${id}/treasury-overview`),
  entities: () => req('/api/entities'),
  transactions: (id, { from, to, accountId, limit = 50, offset = 0 } = {}) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (accountId) qs.set('account_id', String(accountId));
    qs.set('limit', String(limit));
    qs.set('offset', String(offset));
    return req(`/api/entities/${id}/transactions?${qs}`);
  },
  createTransaction: (id, body) => req(`/api/entities/${id}/transactions`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  items: (id) => req(`/api/entities/${id}/items`),

  // Deep-link to the ledger's SPA for a specific entity/view
  deepLink: (entityId, view = 'dashboard') => `${LEDGER_BASE}/e/${entityId}/${view}`,
};

export default ledgerApi;
